export const DEFAULT_APPOINTMENT_DURATION_MINUTES = 60;
export const MAX_APPOINTMENT_DURATION_MINUTES = 24 * 60;

export type AppointmentScheduleIssueCode =
  | 'missing_start'
  | 'missing_end'
  | 'invalid_start'
  | 'invalid_end'
  | 'end_before_start'
  | 'exceeds_max_duration';

export interface AppointmentScheduleValidation {
  isValid: boolean;
  issues: AppointmentScheduleIssueCode[];
  durationMinutes: number | null;
  start: Date | null;
  end: Date | null;
}

export function formatLocalDateTimeValue(date: Date): string {
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, '0');
  const da = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${mo}-${da}T${h}:${mi}`;
}

export function addMinutesToLocalDateTimeValue(value: string, minutes: number): string {
  if (!value) return value;
  const next = new Date(value);
  if (Number.isNaN(next.getTime())) return value;
  next.setMinutes(next.getMinutes() + minutes);
  return formatLocalDateTimeValue(next);
}

function isValidDate(value: Date | null): value is Date {
  return value instanceof Date && !Number.isNaN(value.getTime());
}

export function parseAppointmentDateTime(value?: string | null): Date | null {
  if (!value || typeof value !== 'string') return null;
  const parsed = new Date(value);
  return isValidDate(parsed) ? parsed : null;
}

export function getAppointmentScheduleValidation(
  startValue?: string | null,
  endValue?: string | null,
): AppointmentScheduleValidation {
  const issues: AppointmentScheduleIssueCode[] = [];
  const start = parseAppointmentDateTime(startValue);
  const end = parseAppointmentDateTime(endValue);

  if (!startValue) {
    issues.push('missing_start');
  } else if (!start) {
    issues.push('invalid_start');
  }

  if (!endValue) {
    issues.push('missing_end');
  } else if (!end) {
    issues.push('invalid_end');
  }

  let durationMinutes: number | null = null;

  if (start && end) {
    durationMinutes = Math.round((end.getTime() - start.getTime()) / 60000);

    if (durationMinutes <= 0) {
      issues.push('end_before_start');
    } else if (durationMinutes > MAX_APPOINTMENT_DURATION_MINUTES) {
      issues.push('exceeds_max_duration');
    }
  }

  return {
    isValid: issues.length === 0,
    issues,
    durationMinutes,
    start,
    end,
  };
}

export function normalizeAppointmentDurationMinutes(
  minutes?: number | null,
  defaultDurationMinutes = DEFAULT_APPOINTMENT_DURATION_MINUTES,
): number {
  if (!Number.isFinite(minutes) || !minutes || minutes <= 0) {
    return defaultDurationMinutes;
  }

  return Math.min(Math.round(minutes), MAX_APPOINTMENT_DURATION_MINUTES);
}

export function getAppointmentScheduleErrorMessage(
  startValue?: string | null,
  endValue?: string | null,
): string | null {
  const validation = getAppointmentScheduleValidation(startValue, endValue);

  if (validation.issues.includes('missing_start') || validation.issues.includes('invalid_start')) {
    return 'Please select a valid appointment start time.';
  }

  if (validation.issues.includes('missing_end') || validation.issues.includes('invalid_end')) {
    return 'Please select a valid appointment end time.';
  }

  if (validation.issues.includes('end_before_start')) {
    return 'Appointment end time must be after the start time.';
  }

  if (validation.issues.includes('exceeds_max_duration')) {
    return 'Appointments cannot run for longer than 24 hours.';
  }

  return null;
}

export function normalizeAppointmentSchedule(
  startValue?: string | null,
  endValue?: string | null,
  options?: {
    defaultDurationMinutes?: number;
  },
): {
  scheduled_start: string;
  scheduled_end: string;
  durationMinutes: number;
  adjusted: boolean;
  reason: AppointmentScheduleIssueCode | null;
  validation: AppointmentScheduleValidation;
} {
  const defaultDurationMinutes = normalizeAppointmentDurationMinutes(options?.defaultDurationMinutes);
  const validation = getAppointmentScheduleValidation(startValue, endValue);
  const start = validation.start;

  if (!start || !startValue) {
    return {
      scheduled_start: startValue || '',
      scheduled_end: endValue || '',
      durationMinutes: defaultDurationMinutes,
      adjusted: false,
      reason: validation.issues[0] || null,
      validation,
    };
  }

  let durationMinutes = defaultDurationMinutes;
  let adjusted = false;
  let reason: AppointmentScheduleIssueCode | null = null;

  if (validation.isValid && validation.durationMinutes) {
    durationMinutes = normalizeAppointmentDurationMinutes(validation.durationMinutes, defaultDurationMinutes);
  } else if (validation.issues.includes('exceeds_max_duration')) {
    durationMinutes = MAX_APPOINTMENT_DURATION_MINUTES;
    adjusted = true;
    reason = 'exceeds_max_duration';
  } else if (
    validation.issues.includes('missing_end')
    || validation.issues.includes('invalid_end')
    || validation.issues.includes('end_before_start')
  ) {
    durationMinutes = defaultDurationMinutes;
    adjusted = true;
    reason = validation.issues.includes('end_before_start') ? 'end_before_start' : 'missing_end';
  }

  const normalizedEnd = new Date(start.getTime() + durationMinutes * 60000);

  return {
    scheduled_start: startValue,
    scheduled_end: formatLocalDateTimeValue(normalizedEnd),
    durationMinutes,
    adjusted,
    reason,
    validation,
  };
}

export function formatAppointmentDurationLabel(startValue?: string | null, endValue?: string | null): string {
  const validation = getAppointmentScheduleValidation(startValue, endValue);
  const minutes = normalizeAppointmentDurationMinutes(validation.durationMinutes, DEFAULT_APPOINTMENT_DURATION_MINUTES);
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;

  if (hours > 0 && remainder > 0) return `${hours}h ${remainder}m`;
  if (hours > 0) return `${hours}h`;
  return `${minutes}m`;
}
